// React imports
import React, { useState, useEffect, useCallback, useMemo } from 'react';

// MUI Components
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { grey, green, amber, red } from '@mui/material/colors';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare, faLocationDotSlash } from '@fortawesome/pro-light-svg-icons';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

// Local imports
import PIPELINE from './AccountFunnelPipeline.js';
import PIPELINE_COLORS from './AccountFunnelPipeline.colors.js';
import { getScoreColor, getScoreExplanation } from '../utils/accountScore.js';

dayjs.extend(duration)
dayjs.extend(relativeTime)

function AccountsMainList(props) {
    const { list, accounts = [], opportunities = [], onOpen, selected, setSelected, featureToggle, suppliers = [], mode, accountScores = {}, accountScorer } = props;

    const [pageNumber, setPageNumber] = useState(1);

    // Index opportunities by account id ONCE per opportunities change. The previous
    // implementation filtered the full opportunities list per row (and per account in
    // the entire `list`, even un-rendered ones), which became O(opps × accounts).
    const oppsByAccountId = useMemo(() => {
        const m = new Map();
        for (const o of opportunities) {
            const id = o.account?.id;
            if (!id) continue;
            const arr = m.get(id);
            if (arr) arr.push(o); else m.set(id, [o]);
        }
        return m;
    }, [opportunities]);

    const handleScroll = useCallback(() => {
        const bottom =
            window.innerHeight + window.scrollY >= document.body.offsetHeight - 20;
        if (bottom) {
            setPageNumber(prevPageNumber => {
                if (prevPageNumber * 20 > list.length) return prevPageNumber;
                return prevPageNumber + 1
            });
        }
    }, [ list.length ]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [ list, handleScroll ]);

    // Slice BEFORE mapping — previously list.map(...) generated a node (null) for
    // every account even though only the first pageNumber*20 were rendered. With
    // 26k accounts that produced 26k React fibers per render.
    return list.slice(0, pageNumber * 20).map(e => renderRow({
        account: e, onOpen,
        opportunities: oppsByAccountId.get(e.id) || [],
        selected, setSelected, featureToggle, suppliers, mode, accounts, list,
        score: accountScores[e.id] || (accountScorer ? accountScorer(e) : null),
    }));
}

function renderRow({ account, onOpen, opportunities, selected, setSelected, featureToggle, suppliers = [], mode, accounts = [], list = [], score }) {
    const lastContract = _(opportunities).
        filter(o => o.type === 'contract').
        maxBy(o => o.created);

    const lastOneOff = _(opportunities).
        filter(o => o.type === 'one-off').
        maxBy(o => o.created);

    const lastUpdate = _(account.updates).maxBy(o => o.created);

    // Activity metric — most recent of: update (excluding CW), opportunity, reminder completed
    const nonCwUpdates = (account.updates || []).filter(u => !u.html?.includes('Account updated in Cargowise'));
    const lastNonCwUpdate = _(nonCwUpdates).maxBy(o => o.created);
    const lastActivityDate = _.max(_.compact([
        lastNonCwUpdate?.created,
        lastContract?.created,
        lastOneOff?.created,
        ...(account.reminders || []).filter(r => r.status === 'completed').map(r => new Date(r.dueDate).getTime()),
    ]));
    const daysSinceActivity = lastActivityDate ? dayjs().startOf('day').diff(dayjs(lastActivityDate).startOf('day'), 'day') : null;
    const activityColor = daysSinceActivity === null ? grey[400] : daysSinceActivity <= 7 ? green[600] : daysSinceActivity <= 30 ? amber[700] : red[500];

    const isSupplier = mode === 'suppliers' ? false : (account.uniqueId?.length || 0) < 10 ? false : (suppliers || []).find(s => s.uniqueId === account.uniqueId);
    const isAccount = mode === 'accounts' ? false : (account.uniqueId?.length || 0) < 10 ? false : (accounts || []).find(a => a.uniqueId === account.uniqueId);

    const pipeline = ((account.tags || []).find(t => t.label.startsWith('Pipeline: ')) || { label: `Pipeline: ${PIPELINE[0]}` }).label.substr(10);
    return (
        <Paper key={account.id} sx={{ marginTop: '24px', border: `1px solid ${grey[300]}`}} elevation={0} >
            <Box sx={{ display: 'flex', padding: '6px', alignItems: 'center', borderBottom: `1px solid ${grey[300]}` }}>
                <Checkbox checked={selected.includes(account.id)} onClick={() => setSelected(s => _.xor(s, [account.id]))} />
                <Box sx={{ width: 'auto', paddingRight: '12px' }}>
                    <Chip label={pipeline} size='small' sx={{ fontSize: '11px', fontWeight: 600, color: '#fff', backgroundColor: PIPELINE_COLORS[PIPELINE.indexOf(pipeline)] || '#64748b' }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button variant='text' color='primary' sx={{ color: '#2185D0', textDecoration: 'underline' }} onClick={() => onOpen({ account })}>
                        <Typography variant='h6' sx={{ textDecoration: 'none', fontWeight: 700 }}>
                            {account.name}
                        </Typography>
                    </Button>
                    {score && (() => {
                        const sc = getScoreColor(score.score);
                        return (
                            <Tooltip arrow placement='top' title={
                                <Box sx={{ fontSize: '10px' }}>
                                    {getScoreExplanation(score).map((line, i) => (
                                        <Box key={i} sx={{ fontWeight: i === 0 ? 700 : 400, mb: i === 0 ? 0.5 : 0 }}>{line}</Box>
                                    ))}
                                </Box>
                            }>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, px: '6px', py: '1px', borderRadius: '10px', background: sc.bg, border: `1px solid ${sc.border}`, cursor: 'pointer' }}>
                                    <Typography sx={{ fontSize: '11px', fontWeight: 800, color: sc.color }}>{score.score}</Typography>
                                </Box>
                            </Tooltip>
                        );
                    })()}
                </Box>
                {(!account.address || !account.address.countryCode || account.address.countryCode === 'NA') && (
                    <Typography color='error' variant='caption' sx={{ marginLeft: '6px' }}>
                        <FontAwesomeIcon icon={faLocationDotSlash} />
                        &nbsp;
                        (Address Error)
                    </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Box sx={{ borderLeft: `1px solid ${grey[300]}`}} >
                    <Button sx={{ display: 'flex', alignItems: 'center', textTransform: 'none', color: grey[900] }} onClick={() => onOpen({ account })}>
                        <Typography variant='caption'>
                            Details
                        </Typography>
                    </Button>
                </Box>
                {featureToggle['feature.shipments'] && (
                    <Box sx={{ borderLeft: `1px solid ${grey[300]}`}} >
                        <Button sx={{ display: 'flex', alignItems: 'center', textTransform: 'none', color: grey[900] }} disabled={true} >
                            <Typography variant='caption'>
                                Shipments
                            </Typography>
                        </Button>
                    </Box>
                )}
                <Box sx={{ borderLeft: `1px solid ${grey[300]}`}} >
                    <Button sx={{ display: 'flex', alignItems: 'center', textTransform: 'none', color: grey[900] }} onClick={() => onOpen({ account, subscreen: 'contract' })}>
                        <Typography variant='caption'>
                            Contracts ({opportunities ? opportunities.filter(r => (r.type === 'contract')).length : 0})
                        </Typography>
                    </Button>
                </Box>
                <Box sx={{ borderLeft: `1px solid ${grey[300]}`}} >
                    <Button sx={{ display: 'flex', alignItems: 'center', textTransform: 'none', color: grey[900] }} onClick={() => onOpen({ account, subscreen: 'one-off' })}>
                        <Typography variant='caption'>
                            Price Quotes ({opportunities ? opportunities.filter(r => (r.type === 'one-off')).length : 0})
                        </Typography>
                    </Button>
                </Box>
            </Box>
            <Box sx={{ padding: '6px', display: 'flex', fontWeight: 500, borderBottom: `1px solid ${grey[300]}` }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div style={{ display: 'flex', padding: '6px' }}>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                Created <b>{account.created ? dayjs.duration((account.created - new Date().getTime())/1000, 'seconds').humanize(true) : 'n/a'}</b>
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                &nbsp;|&nbsp;
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                Latest contract <b>{lastContract ? dayjs.duration((lastContract.created - new Date().getTime())/1000, 'seconds').humanize(true) : 'n/a'}</b>
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                &nbsp;|&nbsp;
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                Latest One-off <b>{lastOneOff ? dayjs.duration((lastOneOff.created - new Date().getTime())/1000, 'seconds').humanize(true) : 'n/a'}</b>
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                &nbsp;|&nbsp;
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                Latest Update <b>{lastUpdate ? dayjs.duration((lastUpdate.created - new Date().getTime())/1000, 'seconds').humanize(true) : 'n/a'}</b>
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                &nbsp;|&nbsp;
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                Owner: <b>{account.owner ? account.owner.split('@')[0] : 'n/a'}</b>
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                &nbsp;|&nbsp;
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 600, color: activityColor }}>
                                {daysSinceActivity === null ? 'No activity' : daysSinceActivity === 0 ? '● Active today' : daysSinceActivity <= 7 ? `● Active ${daysSinceActivity}d ago` : `● Last activity ${daysSinceActivity}d ago`}
                            </Typography>
                        </div>
                    </div>
                </Box>
                <Button variant='text' onClick={() => onOpen({ account, newWindow: true })} sx={{ marginTop: '6px', height: '24px', textTransform: 'none' }}>
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    &nbsp;
                    Open in new window
                </Button>
            </Box>
            {((account.tags || []).filter(t => !t.label.startsWith('Pipeline: ')).length > 0 || isSupplier || isAccount) && (
                <Box sx={{ padding: '6px' }} >
                    {account.tags?.filter(t => !t.label.startsWith('Pipeline: ')).map(tag => (<Chip sx={{ marginRight: '6px' }} label={tag.label} key={tag.label} />))}
                    {isSupplier && (
                        <Chip sx={{ marginRight: '6px' }} color='primary' label='Supplier' key='supplier' />
                    )}
                    {isAccount && (
                        <Chip sx={{ marginRight: '6px' }} color='primary' label='Account' key='account' />
                    )}
                </Box>
            )}
        </Paper>
    );
}

export default AccountsMainList;
